import { FC, useState, useEffect, createContext, useContext, Dispatch, SetStateAction, useRef } from 'react'
import { LayoutSplashScreen } from '../../../../_metronic/layout/core'
import { AuthModel, TwoFactorModel, UserModel } from './_models'
import * as authHelper from './AuthHelpers'
import { WithChildren } from '../../../../_metronic/helpers'
import authService from '@/modules/auth/components/auth-service';
import authServiceAdmin from '@/projects/admin/services/auth-service';
import { Config } from '@/core/config/config';
import { jwtDecode } from 'jwt-decode'
import menuService from '@/core/services/menu-service'
import { toast } from 'react-toastify'
import axios from 'axios';
import { userService } from '@/core/services/user-service'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => Promise<void>
  refreshAuth: () => Promise<any>
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: (invalidate?: boolean) => Promise<void>
  impersonate: (username: string) => Promise<void>
  removeImpersonate: () => Promise<void>
  twoFactorAuth: (twoFactorAuth: TwoFactorModel) => Promise<void>
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: async () => { },
  refreshAuth: async (): Promise<any> => { },
  currentUser: undefined,
  setCurrentUser: () => { },
  logout: async (invalidate = true) => { },
  impersonate: async () => { },
  removeImpersonate: async () => { },
  twoFactorAuth: async () => { },
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}


const AuthProvider: FC<WithChildren> = ({ children }) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()

  const saveAuth = async (authModel: AuthModel | undefined) => {

    setAuth(authModel)
    if (authModel) {

      try {
        authHelper.setAuth(authModel)
        var menu = await menuService.getMenuByUser();
        var permissions = await userService.getUserPermissions();
        authHelper.setMenu(menu.data)
        authHelper.setPermissions(permissions.data)
        const user = jwtDecode<UserModel>(authModel.access_token);

        user.language = authModel.language;
        user.culture = authModel.culture;
        user.language_short_code = authModel.language_short_code;
        setCurrentUser(user);
      } catch (error) {
        logout()
        throw error;
      }
    }
    else {
      setCurrentUser(undefined);
      authHelper.removeAuth()
    }
  }

  const refreshAuth = async (): Promise<any> => {

    var currentAuth = authHelper.getAuth()
    if (currentAuth && currentAuth.refresh_token) {
      const params = { refreshToken: currentAuth?.refresh_token };
      const authModel = await authService.refreshToken(params);
      await saveAuth(authModel);
      return authModel;
    } else {
      await saveAuth(undefined);
      return undefined;
    }

  }

  const impersonate = async (username: string) => {

    const impersonatorAuthSession = localStorage.getItem(Config.VITE_APP_AUTH_SESSION);
    localStorage.setItem(Config.VITE_APP_IMPERSONATOR_AUTH_SESSION, impersonatorAuthSession as string)

    const auth = await authServiceAdmin.impersonateUser(username);
    const user = jwtDecode<UserModel>(auth.access_token);
    user.language = auth.language;
    user.culture = auth.culture;
    user.language_short_code = auth.language_short_code;

    await saveAuth(auth);
    window.location.href = "/"
  }

  const removeImpersonate = async () => {
    const impersonatorAuthSession = localStorage.getItem(Config.VITE_APP_IMPERSONATOR_AUTH_SESSION);
    if (impersonatorAuthSession) {
      await saveAuth(JSON.parse(impersonatorAuthSession));
    }
    localStorage.removeItem(Config.VITE_APP_IMPERSONATOR_AUTH_SESSION)
    window.location.href = "/"
  }

  const logout = async (invalidate = true) => {

    const idToken = localStorage.getItem(Config.VITE_APP_ID_TOKEN_KEY as string);
    const impersonatorAuthSession = localStorage.getItem(Config.VITE_APP_IMPERSONATOR_AUTH_SESSION);

    if (impersonatorAuthSession) {
      document.body.classList.add("page-loading");
      const splashScreen = document.getElementById('splash-screen')
      if (splashScreen) {
        splashScreen.style.setProperty('display', 'flex')
      }
      localStorage.removeItem(Config.VITE_APP_IMPERSONATOR_AUTH_SESSION)
      await saveAuth(JSON.parse(impersonatorAuthSession));
      window.location.href = "/"
    } else {
      try {
        if (invalidate) {
          await authService.oAuthLogout(idToken);
        }
      } finally {
        await saveAuth(undefined);
      }
    }
  }

  const twoFactorAuth = async (twoFactorAuth: TwoFactorModel | undefined) => {
    if (twoFactorAuth) {
      authHelper.setTwoFactorAuth(twoFactorAuth);
    }
  }


  return (
    <AuthContext.Provider value={{ auth, saveAuth, refreshAuth, logout, currentUser, setCurrentUser, impersonate, removeImpersonate, twoFactorAuth }}>
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({ children }) => {
  const { auth, saveAuth, setCurrentUser, refreshAuth, logout } = useAuth()
  const [showSplashScreen, setShowSplashScreen] = useState(true)


  function setupAxios(axios: any) {

    axios.defaults.headers = {
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'Expires': '0',
    };
    axios.defaults.headers.Accept = 'application/json'

    axios.interceptors.request.use(async (config:
      {
        headers:
        {
          Authorization: string,
          "X-Client-Id": string
        },
        url: string,
        method: string,
        toastId: any
      }) => {

      const excludedEndpoints = ['refresh-token', 'login', 'check-otp', 'resend-otp'];
      config.headers["X-Client-Id"] = Config.VITE_APP_API_CLIENT_ID;
      if (!excludedEndpoints.includes(config.url.split('/').reverse()[0])) {
        const auth = authHelper.getAuth();
        if (auth && auth.access_token) {
          config.headers.Authorization = `Bearer ${auth.access_token}`;
        }
      }

      return config
    },
      (err: any) => Promise.reject(err)
    );

    axios.interceptors.response.use(
      (response: any) => response,
      async (error: any) => {
        const originalRequest = error.config;
        console.log(error)
        if (error.response && error.response.status === 403) {
          console.log(error.response.data)
          toast.error(`${error.response.data.message}`);
        } else if (error.response && error.response.status === 401) {
          if (!originalRequest._retry) {

            originalRequest._retry = true;
            var result = await refreshAuth();
            originalRequest.headers['Authorization'] = `Bearer ${auth?.access_token}`;
            if (result)
              return axios(originalRequest);

          } else {
            await logout(false);
          }
        } else if (originalRequest._retry) {
          await logout(false);
        }
        return Promise.reject(error);
      }
    );
  }

  useEffect(() => {
    if (auth && auth.access_token) {
      const user = jwtDecode<UserModel>(auth.access_token);
      user.language = auth.language;
      user.culture = auth.culture;
      user.language_short_code = auth.language_short_code;

      setCurrentUser(user);
      setShowSplashScreen(false)
      setupAxios(axios);
    } else {
      saveAuth(undefined).finally(() => {
        setShowSplashScreen(false)
        setupAxios(axios);
      })
    }

  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}



export { AuthProvider, AuthInit, useAuth }
